import { Away, EntityKind, NO_HANDLE, type EntityState } from "odyssey-shared";
import { Targeting, attackable, nextTarget, targetAt, type FeetPoint, type TargetWorld } from "./fight/targeting.ts";
import type { HudFeed } from "./hud/store.ts";
import { gameReport } from "./report.ts";

/**
 * The page's target in the world it draws (P2.2-T3): the selection (`fight/targeting.ts`) read against where the scene
 * draws each character's and dummy's feet, a click's point turned from world pixels into sixteenths, the HUD's target
 * frame fed from the client's view each frame, the bar's target row told the selection (P2.2-T4), and the report's
 * `target`. The session holds one and hands it the frames; the scene hands it the click, Tab and Escape.
 */

/**
 * No view: the selection's world before a session.
 */
const NO_ENTITIES: ReadonlyMap<number, EntityState> = new Map();

/**
 * What the page's targets read of the session and the scene.
 */
export interface PageTargetsHost {
  /** Sends a `target` on the current connection, answering whether it went. */
  readonly send: (handle: number) => boolean;
  /** The client's view, null outside a connection. */
  readonly entities: () => ReadonlyMap<number, EntityState> | null;
  /** The page's own handle, -1 outside a session. */
  readonly own: () => number;
  /** World pixels a sixteenth, 0 before the world has loaded. */
  readonly scale: () => number;
  /**
   * Where the local character's feet are drawn this frame.
   *
   * @param out Where the point is written, in world pixels.
   * @returns False when no character is drawn.
   */
  readonly ownDrawn: (out: FeetPoint) => boolean;
  /**
   * Where another character's feet are drawn this frame, and the top of its head.
   *
   * @param handle Its handle.
   * @param out Where the point and the top are written, in world pixels.
   * @returns False when the page draws no character at the handle.
   */
  readonly drawn: (handle: number, out: FeetPoint) => boolean;
  /**
   * Where a training dummy's feet box stands, and the top of its art.
   *
   * @param handle Its handle.
   * @param out Where the point and the top are written, in sixteenths.
   * @returns False when the page draws no dummy at the handle.
   */
  readonly ground: (handle: number, out: FeetPoint) => boolean;
  /** The HUD's store, told the target each frame. */
  readonly hud: HudFeed;
  /** The bar's store, told the selection each frame, whose target row follows it (P2.2-T4). */
  readonly bar?: { setTarget(handle: number): void };
  /** The name a creature's look gives it (ADR-082 decision 5), by appearance; null for none. */
  readonly creatureName?: (appearance: number) => string | null;
}

/**
 * The page's targets.
 */
export class PageTargets {
  private readonly targeting: Targeting;
  private readonly world: TargetWorld;
  private readonly feetAt: FeetPoint = { x: 0, y: 0, top: 0 };

  /**
   * @param host The session's and the scene's parts the selection reads.
   */
  public constructor(private readonly host: PageTargetsHost) {
    this.targeting = new Targeting({ send: host.send });
    this.world = {
      get entities(): ReadonlyMap<number, EntityState> {
        return host.entities() ?? NO_ENTITIES;
      },
      get own(): number {
        return host.own();
      },
      ownFeet: (out) => this.inSixteenths(host.ownDrawn(out), out),
      feet: (handle, out) => this.inSixteenths(host.drawn(handle, out), out) || host.ground(handle, out),
    };
  }

  /**
   * The selected handle.
   *
   * @returns The handle, `NO_HANDLE` for none.
   */
  public get handle(): number {
    return this.targeting.handle;
  }

  /**
   * Where the selection's feet are drawn, in world pixels, which a click on an ability slot aims at (P2.2-T4).
   *
   * @param out Where the point is written.
   * @returns False when nothing is selected or the page draws nothing at the handle.
   */
  public feet(out: { x: number; y: number }): boolean {
    const handle = this.targeting.handle;
    const scale = this.host.scale();
    if (handle === NO_HANDLE || scale <= 0 || !this.world.feet(handle, this.feetAt)) {
      return false;
    }
    out.x = this.feetAt.x * scale;
    out.y = this.feetAt.y * scale;
    return true;
  }

  /**
   * A left click on the world: selects the character, creature or dummy whose feet box holds the point, and leaves the
   * selection as it is when none does, since the click also swings.
   *
   * @param x The point's x in world pixels.
   * @param y The point's y in world pixels.
   * @param nowMs The page clock in ms.
   * @returns True when the selection changed.
   */
  public selectAt(x: number, y: number, nowMs: number): boolean {
    const scale = this.host.scale();
    if (scale <= 0) {
      return false;
    }
    const handle = targetAt(this.world, x / scale, y / scale);
    return handle !== NO_HANDLE && this.change(handle, nowMs);
  }

  /**
   * The other player whose body holds a point, for the player menu: the entity `selectAt` would select, when it is a
   * player. A creature or a dummy in front of a player hides it, as it does from a click.
   *
   * @param x The point's x in world pixels.
   * @param y The point's y in world pixels.
   * @returns The player's two names, or null when no other player holds the point or the names are not held.
   */
  public playerAt(x: number, y: number): { readonly firstName: string; readonly familyName: string } | null {
    const scale = this.host.scale();
    if (scale <= 0) {
      return null;
    }
    return this.playerOf(targetAt(this.world, x / scale, y / scale));
  }

  /**
   * The other player a handle names, for the player menu opened on a nameplate.
   *
   * @param handle The entity's handle, or a negative number for none.
   * @returns The player's two names, or null when the handle is no other player or its names are not held.
   */
  public playerOf(handle: number): { readonly firstName: string; readonly familyName: string } | null {
    const state = handle === NO_HANDLE || handle < 0 || handle === this.world.own ? undefined : this.host.entities()?.get(handle);
    return state === undefined || state.kind !== EntityKind.Player || state.name.first === "" ? null : { firstName: state.name.first, familyName: state.name.family };
  }

  /**
   * Tab: selects the nearest the player may attack, or the next nearest after the one held; Shift+Tab the other way.
   *
   * @param nowMs The page clock in ms.
   * @param step 1 forward (Tab), -1 backward (Shift+Tab).
   * @returns True when the selection changed.
   */
  public cycle(nowMs: number, step: 1 | -1 = 1): boolean {
    const handle = nextTarget(this.world, this.targeting.handle, step);
    return handle !== NO_HANDLE && this.change(handle, nowMs);
  }

  /**
   * Escape: clears the selection when one is held and the clear can be sent.
   *
   * @param nowMs The page clock in ms.
   * @returns True when the selection was cleared, so the press is taken; false leaves it to the game menu.
   */
  public clear(nowMs: number): boolean {
    return this.targeting.handle !== NO_HANDLE && this.change(NO_HANDLE, nowMs);
  }

  /**
   * Drops the selection when the applied snapshot no longer holds it alive, as the map server does in the same tick,
   * or when it says the page's own character is held, whose target the map server cleared (`Targeting.follow`).
   *
   * @param entities The client's view.
   */
  public follow(entities: ReadonlyMap<number, EntityState>): void {
    if (this.targeting.follow(entities, entities.get(this.host.own())?.away === Away.Held)) {
      gameReport.target = -1;
    }
  }

  /**
   * Tells the HUD's target frame what the client's view holds for the selection, a creature named by its look, and the
   * bar which handle is selected, each frame; each store tells its view only when something it shows changed.
   */
  public show(): void {
    const entities = this.host.entities();
    const handle = this.targeting.handle;
    this.host.bar?.setTarget(handle);
    const state = handle === NO_HANDLE || entities === null ? undefined : entities.get(handle);
    if (state === undefined) {
      this.host.hud.setTarget(null, false);
      return;
    }
    const shown = state.kind === EntityKind.Creature ? (this.host.creatureName?.(state.appearance) ?? "") : undefined;
    this.host.hud.setTarget(state, attackable(entities?.get(this.host.own()), state), shown);
  }

  /**
   * Forgets the selection as a session ends, and empties the frame.
   */
  public reset(): void {
    this.targeting.reset();
    gameReport.target = -1;
    this.host.hud.setTarget(null, false);
  }

  /**
   * Selects a handle or clears the selection, keeping the report's `target` beside it.
   *
   * @param handle The handle, `NO_HANDLE` to clear.
   * @param nowMs The page clock in ms.
   * @returns True when the selection changed.
   */
  private change(handle: number, nowMs: number): boolean {
    const changed = this.targeting.select(handle, nowMs);
    if (changed) {
      gameReport.target = handle === NO_HANDLE ? -1 : handle;
    }
    return changed;
  }

  /**
   * Turns a point and a top the host wrote in world pixels into sixteenths.
   *
   * @param found Whether the host wrote one.
   * @param out The point.
   * @returns `found`.
   */
  private inSixteenths(found: boolean, out: FeetPoint): boolean {
    const scale = this.host.scale();
    if (!found || scale <= 0) {
      return false;
    }
    out.x /= scale;
    out.y /= scale;
    out.top /= scale;
    return true;
  }
}
